import { inject, ref } from 'vue';

const LOCALE_STORAGE_KEY = 'h2o_locale';
const DEFAULT_LOCALE = 'en';
const SUPPORTED_LOCALES = ['en', 'de', 'it', 'fr'];

const enMessages = {
  nav: {
    home: 'Start',
    about: 'About',
    addDiveSite: 'Add Dive Site',
    admin: 'Admin',
    login: 'Login',
    logout: 'Logout',
    editProfile: 'Edit Profile',
    visibility: 'Visibility',
    reportVisibility: 'Report'
  },
  common: {
    edit: 'Edit',
    details: 'Details',
    close: 'Close',
    clear: 'Clear',
    more: 'more...',
    less: 'less',
    deleting: 'Deleting...',
    saving: 'Saving...',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    backToAdmin: 'Back to admin',
    website: 'Website',
    phone: 'Phone',
    address: 'Address',
    street: 'Street',
    zipCode: 'ZIP Code',
    city: 'City',
    country: 'Country',
    latitude: 'Latitude',
    longitude: 'Longitude',
    type: 'Type',
    other: 'Other',
    notes: 'Notes',
    markdownHint: 'Supports line breaks, **bold**, *italic*, and ~~strikethrough~~.',
    heightAboveSeaLevel: 'Height Above Sea Level (m)',
    createdBy: 'Created By',
    updatedBy: 'Updated By',
    name: 'Name',
    selectUser: 'Select a user',
    loadingUsers: 'Loading users...',
    noUsersFound: 'No users found.',
    booking: 'Booking',
    whatsapp: 'WhatsApp',
    overview: 'Overview',
    location: 'Location',
    links: 'Links',
    media: 'Media'
  },
  roles: {
    diver: 'Diver',
    contributor: 'Contributor',
    instructor: 'Instructor',
    admin: 'Admin',
    superuser: 'Superuser',
    superadmin: 'Superadmin'
  },
  auth: {
    loginTitle: 'Login',
    registerTitle: 'Register',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    currentPasswordLabel: 'Current Password',
    newPasswordLabel: 'New Password',
    confirmPasswordLabel: 'Confirm Password',
    confirmNewPasswordLabel: 'Confirm New Password',
    resetTokenLabel: 'Reset Token',
    usernameLabel: 'Username',
    firstNameLabel: 'First Name',
    lastNameLabel: 'Last Name',
    abbreviationLabel: 'Abbreviation',
    loginButton: 'Login',
    registerButton: 'Register',
    noAccount: "Don't have an account?",
    registerHere: 'Register here',
    alreadyHaveAccount: 'Already have an account?',
    loginHere: 'Login here',
    forgotPasswordLink: 'Forgot your password?',
    viewProfilePrefix: 'Or view your',
    userProfileLink: 'User Profile',
    loginFailed: 'Login failed. Please try again.',
    registerFailed: 'Registration failed. Please try again.',
    passwordMismatch: 'Passwords do not match. Please try again.',
    userTitle: 'User',
    welcome: 'Welcome, {firstName} {lastName} ({email})!',
    role: 'Role',
    notes: 'Notes',
    abbreviation: 'Abbreviation',
    accountCreated: 'Account Created',
    lastUpdated: 'Last Updated',
    saveProfile: 'Save Profile',
    changePasswordTitle: 'Change Password',
    changePasswordHelp: 'Enter your current password and choose a new one with at least 6 characters.',
    changePasswordButton: 'Update Password',
    changePasswordRequired: 'Please fill in your current password and the new password twice.',
    passwordMinLength: 'Password must be at least 6 characters long.',
    passwordUpdatedSuccess: 'Password updated successfully.',
    passwordUpdateError: 'Failed to update password.',
    forgotPasswordTitle: 'Forgot Password',
    forgotPasswordHelp: 'Enter your email address to receive a password reset link.',
    forgotPasswordButton: 'Send Reset Link',
    forgotPasswordSuccess: 'If an account with that email exists, a password reset email has been sent.',
    forgotPasswordError: 'Could not request a password reset.',
    resetPasswordTitle: 'Reset Password',
    resetPasswordHelp: 'Set a new password using your password reset link.',
    resetPasswordButton: 'Reset Password',
    resetPasswordRequired: 'Please provide the reset token and enter the new password twice.',
    resetPasswordSuccess: 'Password has been reset successfully.',
    resetPasswordError: 'Could not reset the password.',
    profileUpdatedSuccess: 'Profile updated successfully.',
    profileUpdateError: 'Failed to update profile.'
  },
  main: {
    title: 'H₂O DiveSite Map',
    welcome: 'Welcome to the app.',
    mapTitle: 'Dive Sites Map',
    mapLoading: 'Loading dive sites...',
    mapHint: 'Click a marker to open dive site details.',
    mapNoCoordinates: 'No dive sites with coordinates found.',
    mapError: 'Could not load dive sites map.',
    visibilityOverviewLink: 'View visibility overview',
    reportVisibilityLink: 'Report dive visibility',
    filterByName: 'Filter by name',
    filterByNamePlaceholder: 'Type a name...',
    filterByWaterBody: 'Filter dive sites by water body',
    allWaterBodies: 'All water bodies',
    loadingWaterBodies: 'Loading water bodies...',
    loadWaterBodiesError: 'Could not load water bodies.',
    filterResultsCount: '{count} matching items',
    filterResultsEmpty: 'No matching map items.',
    mapFiltersTitle: 'Map Filters',
    centerOnCurrentPosition: 'Center on current position',
    centerOnCurrentPositionShort: '◎',
    filterTypesTitle: 'Type',
    filterTypes: {
      all: 'All',
      diveSite: 'Dive Site',
      hospital: 'Hospital',
      pharmacy: 'Pharmacy',
      aed: 'AED',
      airFillingStation: 'Air Filling Station',
      rentalEquipment: 'Rental Equipment',
      shop: 'Shop',
      sponsor: 'Sponsor',
      restaurant: 'Restaurant'
    },
    planning: {
      title: 'Dive Planner',
      subtitle: 'Find dive sites with enough parking for your group.',
      diversLabel: 'Number of vehicles',
      difficultyLabel: 'Difficulty level',
      selectDifficulty: 'Select difficulty',
      targetWaterBody: 'Target water body',
      selectWaterBody: 'Select water body',
      startButton: 'Start planning',
      clearButton: 'Clear plan',
      validationError: 'Please enter divers, difficulty level, and a target water body.',
      permissionError: 'You do not have permission to use dive planning.',
      resultsTitle: 'Possible dive sites',
      resultsCount: '{count} matching dive sites',
      noResults: 'No dive sites match this plan yet.',
      parkingCapacity: '{count} parking spaces',
      descriptionFallback: 'No short description available.'
    }
  },
  about: {
    kicker: 'About',
    title: 'About H₂O DiveSite Map',
    paragraphs: [
      'Dear diving friends',
      'We all know the situation: you plan a dive at a new site, look for information, and either find nothing, outdated details, or have to gather everything piece by piece. That is exactly what we want to change.',
      'We are a small team from H2O DIE Tauchschule, working to bring all dive sites in Switzerland together on one shared platform, with everything you need to know before entering the water. Our home is Lake Walensee, which we know very well. But the Swiss lakes have so much more to offer, and that knowledge often lives in the minds of people like you.',
      'Is a dive site missing? Is something incorrect? Do you have a tip that others should not miss? Write to us, because every contribution that helps improve the safety of other divers counts.'
    ],
    signoff: 'Good air always and lots of joy in the water!',
    team: 'Your team from H2O DIE Tauchschule'
  },
  visibility: {
    noValue: 'No value',
    levels: {
      verybad: 'Very bad',
      bad: 'Bad',
      poor: 'Poor',
      okay: 'Okay',
      good: 'Good',
      verygood: 'Very good',
      excellent: 'Excellent'
    },
    depths: {
      '0to5': '0 to 5 meters',
      '5to10': '5 to 10 meters',
      '10to20': '10 to 20 meters',
      '20to30': '20 to 30 meters',
      '30to40': '30 to 40 meters',
      moreThan40: 'Below 40 meters'
    },
    report: {
      title: 'Report Dive Visibility',
      toOverview: 'Go to visibility overview',
      filterByName: 'Filter dive site by name',
      filterByNamePlaceholder: 'Type a dive site name...',
      filterResultsCount: '{count} matching dive sites',
      filterResultsEmpty: 'No matching dive sites.',
      selectDiveSite: 'Select a dive site',
      loadingDiveSites: 'Loading dive sites...',
      availableDiveSites: '{count} dive sites available',
      dateOfDive: 'Date of dive',
      reportingAs: 'Reporting as',
      yourName: 'Your name',
      yourEmail: 'Your email',
      abbreviation: 'Abbreviation',
      reportedBy: 'Reported by',
      can: 'You can',
      registerLink: 'register',
      or: 'or',
      loginLink: 'login',
      profileHint: 'to report with your profile.',
      divers: 'Divers',
      notes: 'Notes',
      submitting: 'Submitting...',
      submit: 'Submit visibility report',
      loadDiveSitesError: 'Could not load dive sites.',
      selectDiveSiteError: 'Please select a dive site.',
      selectVisibilityLevelError: 'Please select at least one visibility depth.',
      nameEmailRequired: 'Name and email are required.',
      humanCheckNotice: 'This form is protected by reCAPTCHA.',
      humanCheckFailed: 'Human verification failed. Please try again.',
      humanCheckUnavailable: 'Human verification is currently unavailable.',
      submitSuccess: 'Visibility report submitted.',
      submitError: 'Could not submit visibility report.'
    },
    overview: {
      title: 'Visibility Overview',
      subtitle: 'Showing reports from the last 14 days.',
      reportLink: 'Report dive visibility',
      activeFilter: 'A filter is active.',
      activeWaterBodyFilter: 'Filtered by water body: {name}',
      loading: 'Loading visibility overview...',
      noReports: 'No reports in the last two weeks.',
      latest: 'Latest',
      deleting: 'Deleting...',
      loadError: 'Could not load visibility overview.',
      deleteConfirm: 'Delete this visibility report?',
      deleteError: 'Could not delete report.',
      columns: {
        diveSite: 'Dive site',
        date: 'Date',
        remarks: 'Info',
        delete: 'Delete'
      }
    }
  },
  map: {
    helpReady: 'Click on the map to set latitude and longitude.',
    loadingLocation: 'Getting your location...',
    locationHint: 'Click map to pick coordinates.',
    geolocationUnsupported: 'Geolocation is not supported by your browser.',
    locationAccessDenied: 'Location access denied. Using fallback position.',
    mapAssetsError: 'Could not load map assets.'
  },
  diveSite: {
    addTitle: 'Add Dive Site',
    requiredFieldsError: 'Please fill in Created By ID, Name and Dive Site Description.',
    createError: 'Could not create dive site.',
    createdById: 'Created By ID',
    diveSiteName: 'Dive Site Name',
    diveSiteDescription: 'Dive Site Description',
    alternativeName1: 'Alternative Name',
    alternativeName2: 'Alternative Name',
    linkedWaterBody: 'Linked Water Body',
    noLinkedWaterBody: 'No linked water body',
    loadingLinkedWaterBodies: 'Loading water bodies...',
    linkedWaterBodiesAvailable: '{count} water bodies available',
    loadLinkedWaterBodiesError: 'Could not load water bodies.',
    entryPoint: 'Entry',
    pickCoordinates: 'Pick Coordinates on Map',
    latitude: 'Latitude',
    longitude: 'Longitude',
    heightAboveSeaLevel: 'Height Above Sea Level (m)',
    difficultyLevel: 'Difficulty Level',
    difficultyNone: 'No difficulty selected',
    uploadMapImage1: 'Upload Map Image 1',
    uploadMapImage2: 'Upload Map Image 2',
    uploadMapImageBathymetryNote: 'If the filename starts with "b3d", a color legend for the swissBATHY3D map is added automatically.',
    uploadingImage1: 'Uploading image 1...',
    uploadingImage2: 'Uploading image 2...',
    mapDetails1: 'Map Details 1 (ObjectId)',
    mapDetails2: 'Map Details 2 (ObjectId)',
    editMapDetails1: 'Edit Map Details 1',
    editMapDetails2: 'Edit Map Details 2',
    highlights: 'Here you find',
    emergencyHospital: 'Emergency Hospital',
    noEmergencyHospitalSelected: 'No emergency hospital selected',
    loadingHospitals: 'Loading hospitals...',
    hospitalsAvailable: '{count} hospitals available',
    hospitalMapHint: 'Click a hospital marker on the map to select it.',
    hospitalMapRequiresCoordinates: 'Set the dive site coordinates first to choose a hospital on the map.',
    hospitalMapVisibleCount: 'Showing {count} hospital markers.',
    hospitalMapNoNearbyFacilities: 'No nearby hospitals available for this dive site.',
    selectedHospital: 'Selected hospital',
    clearSelectedHospital: 'Clear hospital selection',
    emergencyNextAed: 'Emergency Next AED',
    noEmergencyNextAedSelected: 'No AED selected',
    loadingAedFacilities: 'Loading AED facilities...',
    aedFacilitiesAvailable: '{count} AED facilities available',
    aedMapHint: 'Click an AED marker on the map to select it.',
    aedMapRequiresCoordinates: 'Set the dive site coordinates first to choose an AED on the map.',
    aedMapVisibleCount: 'Showing {count} AED markers.',
    aedMapNoNearbyFacilities: 'No nearby AEDs available for this dive site.',
    selectedAed: 'Selected AED',
    clearSelectedAed: 'Clear AED selection',
    emergencyPhone: 'Phone',
    emergencyPhoneName: 'Emergency Phone Name',
    emergencyPhoneNamePlaceholder: 'Coast Guard, Rescue Center, etc.',
    facilitiesLabel: 'Facilities',
    dangers: 'Dangers',
    directionsDescription: 'Directions Description',
    accessibilityOtherLabel: 'Accessibility Other',
    loadFacilitiesError: 'Could not load facilities.',
    notes: 'Notes',
    byCar: 'Accessible by Car',
    byBoat: 'Accessible by Boat',
    saving: 'Saving...',
    createButton: 'Create Dive Site',
    form: {
      sections: {
        access: 'Access & Facilities',
        notes: 'Notes & Safety'
      },
      parkingSpaces: 'Parking spaces',
      recalculateParkingDistance: '↻',
      parkingDistanceHint: 'Use recalculate to fill this from dive site and parking coordinates. You can still adjust it.'
    },
    show: {
      backToMap: 'Back to map',
      loading: 'Loading dive site...',
      fallbackTitle: 'Dive Site',
      deleteDiveSite: 'Delete Dive Site',
      downloadPdfSummary: 'Download PDF',
      pdfPreparing: 'Preparing PDF...',
      pdfSummaryTitle: 'Dive site summary',
      pdfMapMarkersTitle: 'Map markers',
      qrCodeAlt: 'QR code to this dive site',
      pdfHospitalAddress: 'Hospital address',
      pdfHospitalPhone: 'Hospital phone',
      sections: {
        overview: 'Overview',
        location: 'Location',
        accessibilityFacilities: 'Accessibility & Facilities',
        linkedData: 'Linked Data',
        visibility: 'Visibility',
        maps: 'Maps',
        metadata: 'Metadata'
      },
      description: 'Description',
      alternativeName1: 'Alternative Name',
      alternativeName2: 'Alternative Name',
      entryPoint: 'Entry point',
      dangers: 'Dangers',
      directions: 'Directions',
      notes: 'Notes',
      difficultyLevel: 'Difficulty Level',
      difficulty: {
        beginner: 'Easy',
        advanced: 'Moderate',
        expert: 'Advanced'
      },
      location: 'Location',
      coordinates: 'Coordinates',
      nextParking: 'Next Parking',
      nextParkingLatitude: 'Parking Latitude',
      nextParkingLongitude: 'Parking Longitude',
      parkingType: 'Parking',
      parkingPosition: 'Parking Position',
      parkingPositionTitle: 'Parking position',
      navigateToDiveSite: 'navigate to this divesite',
      navigateToParking: 'Navigate to this parking',
      bearingNavigation: {
        title: 'Bearing navigation',
        subtitle: 'Live direction to the dive site',
        targetDirection: 'Target {direction}',
        enableCompass: 'Enable compass',
        calibrateCompass: 'Calibrate compass',
        locating: 'Finding your position...',
        locationUnsupported: 'Live location is not supported by your browser.',
        compassUnavailable: 'Compass data is not available on this device.',
        compassPermissionHint: 'Allow compass access to show a live direction arrow.',
        calibrationHint: 'Calibrate the compass if the arrow jumps. Move the phone in a figure eight.',
        cardinals: {
          north: 'north',
          northeast: 'northeast',
          east: 'east',
          southeast: 'southeast',
          south: 'south',
          southwest: 'southwest',
          west: 'west',
          northwest: 'northwest'
        }
      },
      heightAboveSeaLevel: 'Height Above Sea Level',
      byCar: 'Accessible by car',
      byBoat: 'Accessible by boat',
      accessibilityOther: 'Accessibility Other',
      onSiteFacilities: 'On-site Facilities',
      highlights: 'Here you find',
      waterBodyName: 'Water body name',
      waterBodyType: 'Water body type',
      waterType: 'Water type',
      emergencyFacility: 'Next Facility',
      emergencyType: 'Type',
      emergencyLocation: 'Location',
      emergencyPhone: 'Phone',
      emergencyWebsite: 'Website',
      emergencyNextAed: 'Next AED',
      aedLocation: 'Location',
      emergencyPhoneName: 'Emergency Phone Name',
      safetyInfos: 'Safety Infos',
      emergencyInfos: 'Emergency Infos',
      saveChanges: 'Save changes',
      boatsTitle: 'Boat Trips',
      loadingBoats: 'Loading boat operators...',
      loadBoatsError: 'Could not load boat operators.',
      noBoatOperators: 'No boat operators are linked to this water body yet.',
      bookBoatTrip: 'Book boat trip',
      operatorName: 'Operator',
      boatLogoAlt: 'Operator logo',
      boatImageAlt: 'Boat image',
      openMap: 'Open map',
      colorLegend: 'Color Legend',
      bathymetryLegendAlt: 'Bathymetry color legend',
      mapImage1Alt: 'Map 1',
      mapImage2Alt: 'Map 2',
      mapImage1LoadError: 'Could not load image 1.',
      mapImage2LoadError: 'Could not load image 2.',
      loadingMapDetails: 'Loading map details...',
      loadMapDetailsError: 'Could not load map details.',
      uploadImageError: 'Could not upload image.',
      updateError: 'Could not update dive site.',
      deleteConfirm: 'Delete this dive site? This action cannot be undone.',
      deleteError: 'Could not delete dive site.',
      deleteMapDetailsConfirm: 'Delete {target}? This removes the map image and linked map objects after you save changes.',
      openNavigationApp: 'Open in navigation app',
      visibility: {
        loading: 'Loading visibility reports...',
        none: 'No visibility reports available.',
        sourceLastTwoWeeks: 'Showing reports from the last two weeks.',
        sourceLatestFive: 'No reports in the last two weeks. Showing 5 newest reports.',
        infoButton: 'info'
      },
      mapImage1: 'Map Image 1',
      mapImage2: 'Map Image 2',
      mapObject: 'Map object',
      diveSitePosition: 'Dive site position',
      emergencyFacilityPosition: 'Emergency facility position',
      emergencyNextAedPosition: 'Emergency AED position',
      mapObjectPicture1: 'Map object picture 1',
      mapObjectPicture2: 'Map object picture 2',
      note1: 'Note 1',
      note2: 'Note 2',
      noLinkedPicturesOrNotes: 'No linked pictures or notes for this map object.',
      noMapImages: 'No map images available.',
      notice: {
        warning: 'Info',
        reminderTitle: 'Important information',
        continueButton: 'Continue to dive site',
        addTitle: 'Add notice',
        editTitle: 'Edit notice',
        titleLabel: 'Title',
        infoLabel: 'Information',
        startDateLabel: 'Start date',
        endDateLabel: 'End date (optional)',
        noEndDate: 'No end date',
        dateRange: '{start} to {end}',
        saveError: 'Could not save notice.',
        deleteConfirm: 'Delete this notice? This action cannot be undone.',
        deleteError: 'Could not delete notice.'
      },
      mapDetails1Id: 'Map Details 1 ID',
      mapDetails2Id: 'Map Details 2 ID',
      createdBy: 'Created By',
      updatedBy: 'Updated By',
      created: 'Created',
      updated: 'Updated',
      yes: 'Yes',
      no: 'No',
      parkingDistance: 'Parking Distance',
      highlightValues: {
        wreck: 'Wreck',
        wall: 'Wall',
        overhang: 'Overhang',
        cave: 'Cave',
        rocks: 'Rocks',
        object: 'Object',
        plants: 'Plants',
        silt: 'Silt'
      },
      facilityValues: {
        parkingFree: 'Parking Free',
        parkingPaid: 'Parking Paid',
        toilet: 'Toilet',
        shelter: 'Shelter',
        kiosk: 'Kiosk',
        restaurant: 'Restaurant',
        foodTruck: 'Food Truck',
        rentalEquipment: 'Rental Equipment',
        airFillingStation: 'Air Filling Station'
      },
      notFound: 'Dive site not found.',
      loadError: 'Could not load dive site details.',
      waterBodyTypes: {
        lake: 'Lake',
        river: 'River',
        sea: 'Sea',
        ocean: 'Ocean',
        pool: 'Pool'
      },
      waterTypes: {
        freshwater: 'Freshwater',
        saltwater: 'Saltwater',
        brackish: 'Brackish',
        chlorinated: 'Chlorinated'
      }
    }
  },
  language: {
    label: 'Language',
    english: 'English',
    german: 'German',
    italian: 'Italian',
    french: 'French'
  },
  theme: {
    label: 'Theme',
    system: 'System',
    light: 'Light',
    dark: 'Dark'
  },
  admin: {
    title: 'Admin',
    intro: 'Select the entry you want to edit or delete.',
    openStatsPage: 'Open statistics',
    publicAccessTitle: 'Public app access',
    publicAccessHelp: 'Require visitors to log in before they can use the dive site map, dive site details, or visibility pages.',
    publicAccessOpen: 'Open to guests',
    publicAccessLoginRequired: 'Login required',
    loadingSettings: 'Loading settings...',
    savingSettings: 'Saving settings...',
    loadSettingsError: 'Could not load settings.',
    updateSettingsError: 'Could not update settings.',
    importTitle: 'Import .dlexch',
    importIntro: 'Upload a DiveLogExchange file to create dive sites in bulk.',
    importFileLabel: '.dlexch file',
    importNoFileSelected: 'No file selected.',
    importNoFileError: 'Please choose a .dlexch file first.',
    importButton: 'Import file',
    importing: 'Importing...',
    importProcessedCount: 'Place items',
    importImportedCount: 'Imported',
    importIgnoredCount: 'Ignored',
    importWarningCount: 'Warnings',
    importErrorCount: 'Errors',
    importWarningsTitle: 'Import warnings',
    importErrorsTitle: 'Import errors',
    importError: 'Could not import the .dlexch file.',
    usersTitle: 'Users',
    waterBodiesTitle: 'Water Bodies',
    diveSitesTitle: 'Dive Sites',
    facilitiesTitle: 'Facilities',
    boatsTitle: 'Boats',
    noUsersFound: 'No users found.',
    noWaterBodiesFound: 'No water bodies found.',
    noDiveSitesFound: 'No dive sites found.',
    noFacilitiesFound: 'No facilities found.',
    noBoatsFound: 'No boats found.',
    addWaterBody: 'Add Water Body',
    addDiveSite: 'Add Dive Site',
    addFacility: 'Add Facility',
    addBoat: 'Add Boat',
    filterUsers: 'Filter users',
    filterUsersPlaceholder: 'Name, email or role',
    loadingUsers: 'Loading users...',
    filterWaterBodies: 'Filter water bodies',
    filterWaterBodiesPlaceholder: 'Name or location',
    loadingWaterBodies: 'Loading water bodies...',
    filterDiveSites: 'Filter dive sites',
    filterDiveSitesPlaceholder: 'Name or location',
    loadingDiveSites: 'Loading dive sites...',
    filterFacilities: 'Filter facilities',
    filterFacilitiesPlaceholder: 'Name or location',
    loadingFacilities: 'Loading facilities...',
    filterBoats: 'Filter boats',
    filterBoatsPlaceholder: 'Boat, operator or water body',
    loadingBoats: 'Loading boats...',
    loadUsersError: 'Could not load users.',
    loadWaterBodiesError: 'Could not load water bodies.',
    loadDiveSitesError: 'Could not load dive sites.',
    loadFacilitiesError: 'Could not load facilities.',
    loadBoatsError: 'Could not load boats.',
    userDetailsTitle: 'User Details',
    loadingUser: 'Loading user...',
    userNotFound: 'User not found.',
    loadUserError: 'Could not load user details.',
    noEditPermission: 'You do not have permissions to edit this user.',
    changePasswordTitle: 'Change Password',
    changePasswordHelp: 'Set a new password for this user. The password must be at least 6 characters long.',
    saveUser: 'Save user',
    savePassword: 'Save password',
    deleteUser: 'Delete user',
    deleteUserConfirm: 'Delete this user permanently?',
    passwordRequired: 'Please enter the new password twice.',
    passwordMinLength: 'Password must be at least 6 characters long.',
    updateUserSuccess: 'User updated successfully.',
    updateUserError: 'Could not update user.',
    updatePasswordSuccess: 'Password updated successfully.',
    updatePasswordError: 'Could not update password.',
    deleteUserError: 'Could not delete user.',
    statsTitle: 'Statistics',
    statsIntro: 'Online values show activity seen in the last {minutes} minutes.',
    statsRefresh: 'Refresh statistics',
    statsLoading: 'Loading statistics...',
    statsLoadError: 'Could not load statistics.',
    statsGeneratedAt: 'Last updated',
    statsOnlineTitle: 'Currently online',
    statsOnlineSessions: 'Active sessions',
    statsOnlineAuthenticatedUsers: 'Signed-in users',
    statsOnlineGuests: 'Guest sessions',
    statsOnlineAdmins: 'Admins online',
    statsOnlineSuperusers: 'Superusers online',
    statsDatabaseTitle: 'Database overview',
    statsSponsorFacilities: 'Sponsor facilities',
    statsSponsorLinksAvailable: 'Sponsor links',
    statsBoatsWithBookingLinks: 'Boats with trip/contact links',
    statsBoatClicksTitle: 'Boat trip link clicks',
    statsSponsorClicksTitle: 'Sponsor link clicks',
    statsBoatClicksTableTitle: 'Boat trip link breakdown',
    statsSponsorClicksTableTitle: 'Sponsor link breakdown',
    statsTotalClicks: 'Total clicks',
    statsUniqueVisitors: 'Unique visitors',
    statsLast30Days: 'Last 30 days',
    statsLast7Days: 'Last 7 days',
    statsTarget: 'Target link',
    statsOperator: 'Operator',
    statsNoBoatLinks: 'No tracked boat links found.',
    statsNoSponsorLinks: 'No sponsor links found.'
  },
  waterBody: {
    titleAdd: 'Add Water Body',
    titleEdit: 'Edit Water Body',
    submitCreate: 'Create Water Body',
    submitUpdate: 'Update Water Body',
    nameLabel: 'Water Body Name',
    fallbackTitle: 'Water body',
    typeLabel: 'Type',
    waterTypeLabel: 'Water Type',
    pickCoordinates: 'Pick Coordinates on Map',
    loadUsersError: 'Could not load users.',
    loadError: 'Could not load water body.',
    createError: 'Could not create water body.',
    updateError: 'Could not update water body.',
    requiredWithUser: 'Please select Created By user and fill in Name.',
    requiredName: 'Please fill in Name.',
    unsavedTitle: 'Discard unsaved changes?',
    unsavedText: 'You have unsaved changes. If you leave now, your changes will be lost.',
    leavePage: 'Leave page',
    stayPage: 'Stay on page',
    types: {
      lake: 'Lake',
      river: 'River',
      sea: 'Sea',
      ocean: 'Ocean',
      pool: 'Pool',
      other: 'Other'
    },
    waterTypes: {
      freshwater: 'Freshwater',
      saltwater: 'Saltwater',
      brackish: 'Brackish',
      chlorinated: 'Chlorinated'
    }
  },
  facility: {
    titleAdd: 'Add Facility',
    titleEdit: 'Edit Facility',
    submitCreate: 'Create Facility',
    submitUpdate: 'Update Facility',
    nameLabel: 'Facility Name',
    fallbackTitle: 'Facility',
    pickCoordinates: 'Pick Coordinates on Map',
    loadUsersError: 'Could not load users.',
    loadError: 'Could not load facility.',
    createError: 'Could not create facility.',
    updateError: 'Could not update facility.',
    requiredWithUser: 'Please select Created By user and fill in Name.',
    requiredName: 'Please fill in Name.',
    logoFileLabel: 'Logo File',
    logoFilePlaceholder: 'example-logo.svg',
    logoFileHelp: 'Filename from /sponsors, including .png or .svg.'
  },
  boat: {
    titleAdd: 'Add Boat',
    titleEdit: 'Edit Boat',
    submitCreate: 'Create Boat',
    submitUpdate: 'Update Boat',
    nameLabel: 'Boat Name',
    operatorNameLabel: 'Operator Name',
    waterBodyLabel: 'Linked Water Body',
    selectWaterBody: 'Select a water body',
    pickCoordinates: 'Pick Coordinates on Map',
    webLinkLabel: 'Operator Website',
    logoUrlLabel: 'Logo URL',
    logoUploadLabel: 'Upload Logo',
    boatImageUrlLabel: 'Boat Image URL',
    boatImageUploadLabel: 'Upload Boat Image',
    whatsappLinkLabel: 'WhatsApp Link',
    bookingLinkLabel: 'Booking Link',
    phoneLinkLabel: 'Phone Link',
    uploadingLogo: 'Uploading logo...',
    uploadingBoatImage: 'Uploading boat image...',
    uploadImageError: 'Could not upload image.',
    logoPreviewAlt: 'Logo preview',
    boatImagePreviewAlt: 'Boat image preview',
    deleteLogo: 'Delete logo',
    deleteBoatImage: 'Delete boat image',
    imagePathPlaceholder: '/api/v1/map-details/.../image or https://...',
    imagePathHelp: 'Optional. Uploaded images fill this automatically. Relative /api/v1/... paths are allowed.',
    loadUsersError: 'Could not load users.',
    loadWaterBodiesError: 'Could not load water bodies.',
    loadError: 'Could not load boat.',
    createError: 'Could not create boat.',
    updateError: 'Could not update boat.',
    requiredWithUser: 'Please select Created By user and fill in boat name and linked water body.',
    requiredNameAndWaterBody: 'Please fill in boat name and linked water body.'
  },
  mapDetailsEditor: {
    defaultTitle: 'Edit map details',
    intro: 'Click the image to add a map object. Click a marker to edit it.',
    noMapDetailImage: 'Upload or set a map detail image first.',
    loadingObjects: 'Loading map objects...',
    imageAlt: 'Map details',
    objectFallback: 'Map object',
    imageMissing: 'Map image URL is missing. Upload a map image first.',
    imageLoadError: 'Could not load map image.',
    editObject: 'Edit map object',
    newObject: 'New map object',
    positionOnMap: 'Position on map (%)',
    uploadPicture1: 'Upload Picture 1',
    uploadPicture2: 'Upload Picture 2',
    uploadingPicture1: 'Uploading picture 1...',
    uploadingPicture2: 'Uploading picture 2...',
    picture1Preview: 'Picture 1 preview',
    picture2Preview: 'Picture 2 preview',
    deletePicture1: 'Delete picture 1',
    deletePicture2: 'Delete picture 2',
    picture1Note: 'Picture 1 Note',
    picture2Note: 'Picture 2 Note',
    saveObject: 'Save map object',
    selectHint: 'Select a marker or click the map image to add a map object.',
    loadObjectsError: 'Could not load map objects.',
    uploadNoLinkError: 'Upload did not return a link.',
    uploadError: 'Could not upload picture.',
    missingMapDetailId: 'No map detail id is set.',
    nameRequired: 'Name is required.',
    coordinatesRequired: 'Coordinates must be numbers.',
    saveError: 'Could not save map object.',
    deleteError: 'Could not delete map object.'
  }
};

const deMessages = {
  nav: {
    home: 'Start',
    about: 'Über uns',
    addDiveSite: 'Tauchplatz hinzufügen',
    admin: 'Admin',
    login: 'Anmelden',
    logout: 'Abmelden',
    editProfile: 'Profil bearbeiten',
    visibility: 'Sichtweiten',
    reportVisibility: 'Melden'
  },
  common: {
    edit: 'Bearbeiten',
    details: 'Details',
    close: 'Schliessen',
    clear: 'Leeren',
    more: 'mehr...',
    less: 'weniger',
    deleting: 'Wird gelöscht...',
    saving: 'Wird gespeichert...',
    cancel: 'Abbrechen',
    save: 'Speichern',
    delete: 'Löschen',
    backToAdmin: 'Zurück zum Admin',
    website: 'Webseite',
    phone: 'Telefon',
    address: 'Adresse',
    street: 'Strasse',
    zipCode: 'PLZ',
    city: 'Ort',
    country: 'Land',
    latitude: 'Breitengrad',
    longitude: 'Längengrad',
    type: 'Typ',
    other: 'Andere',
    notes: 'Notizen',
    markdownHint: 'Unterstuetzt Zeilenumbrueche, **fett**, *kursiv* und ~~durchgestrichen~~.',
    heightAboveSeaLevel: 'Höhe über Meeresspiegel (m)',
    createdBy: 'Erstellt von',
    updatedBy: 'Aktualisiert von',
    name: 'Name',
    selectUser: 'Benutzer auswählen',
    loadingUsers: 'Benutzer werden geladen...',
    noUsersFound: 'Keine Benutzer gefunden.',
    booking: 'Buchung',
    whatsapp: 'WhatsApp',
    overview: 'Übersicht',
    location: 'Ort',
    links: 'Links',
    media: 'Medien'
  },
  roles: {
    diver: 'Taucher',
    contributor: 'Mitwirkender',
    instructor: 'Instruktor',
    admin: 'Admin',
    superuser: 'Superuser',
    superadmin: 'Superadmin'
  },
  auth: {
    loginTitle: 'Anmelden',
    registerTitle: 'Registrieren',
    emailLabel: 'E-Mail',
    passwordLabel: 'Passwort',
    currentPasswordLabel: 'Aktuelles Passwort',
    newPasswordLabel: 'Neues Passwort',
    confirmPasswordLabel: 'Passwort bestätigen',
    confirmNewPasswordLabel: 'Neues Passwort bestätigen',
    resetTokenLabel: 'Reset-Token',
    usernameLabel: 'Benutzername',
    firstNameLabel: 'Vorname',
    lastNameLabel: 'Nachname',
    abbreviationLabel: 'Kürzel',
    loginButton: 'Anmelden',
    registerButton: 'Registrieren',
    noAccount: 'Noch kein Konto?',
    registerHere: 'Hier registrieren',
    alreadyHaveAccount: 'Bereits ein Konto?',
    loginHere: 'Hier anmelden',
    forgotPasswordLink: 'Passwort vergessen?',
    viewProfilePrefix: 'Oder dein',
    userProfileLink: 'Benutzerprofil',
    loginFailed: 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.',
    registerFailed: 'Registrierung fehlgeschlagen. Bitte erneut versuchen.',
    passwordMismatch: 'Passwoerter stimmen nicht ueberein. Bitte erneut versuchen.',
    userTitle: 'Benutzer',
    welcome: 'Willkommen, {firstName} {lastName} ({email})!',
    role: 'Rolle',
    notes: 'Notizen',
    abbreviation: 'Kürzel',
    accountCreated: 'Konto erstellt',
    lastUpdated: 'Zuletzt aktualisiert',
    saveProfile: 'Profil speichern',
    changePasswordTitle: 'Passwort ändern',
    changePasswordHelp: 'Gib dein aktuelles Passwort ein und wähle ein neues mit mindestens 6 Zeichen.',
    changePasswordButton: 'Passwort aktualisieren',
    changePasswordRequired: 'Bitte aktuelles Passwort und das neue Passwort zweimal eingeben.',
    passwordMinLength: 'Das Passwort muss mindestens 6 Zeichen lang sein.',
    passwordUpdatedSuccess: 'Passwort erfolgreich aktualisiert.',
    passwordUpdateError: 'Passwort konnte nicht aktualisiert werden.',
    forgotPasswordTitle: 'Passwort vergessen',
    forgotPasswordHelp: 'Gib deine E-Mail-Adresse ein, damit du einen Link zum Zurücksetzen erhältst.',
    forgotPasswordButton: 'Reset-Link senden',
    forgotPasswordSuccess: 'Falls ein Konto mit dieser E-Mail existiert, wurde eine E-Mail zum Zurücksetzen gesendet.',
    forgotPasswordError: 'Passwort-Reset konnte nicht angefordert werden.',
    resetPasswordTitle: 'Passwort zurücksetzen',
    resetPasswordHelp: 'Setze mit deinem Link zum Zurücksetzen ein neues Passwort.',
    resetPasswordButton: 'Passwort zurücksetzen',
    resetPasswordRequired: 'Bitte Reset-Token und das neue Passwort zweimal eingeben.',
    resetPasswordSuccess: 'Das Passwort wurde erfolgreich zurückgesetzt.',
    resetPasswordError: 'Passwort konnte nicht zurückgesetzt werden.',
    profileUpdatedSuccess: 'Profil erfolgreich aktualisiert.',
    profileUpdateError: 'Profil konnte nicht aktualisiert werden.'
  },
  main: {
    ...enMessages.main,
    title: 'H₂O Tauchplatz App',
    welcome: 'Willkommen in der App.',
    mapTitle: 'Tauchplatzkarte',
    mapLoading: 'Tauchplaetze werden geladen...',
    mapHint: 'Klicke auf einen Marker, um Tauchplatzdetails zu sehen.',
    mapNoCoordinates: 'Keine Tauchplaetze mit Koordinaten gefunden.',
    mapError: 'Tauchplatzkarte konnte nicht geladen werden.',
    visibilityOverviewLink: 'Sichtweiten-Übersicht',
    reportVisibilityLink: 'Sichtweite melden',
    filterByName: 'Nach Name filtern',
    filterByNamePlaceholder: 'Name eingeben...',
    filterByWaterBody: 'Tauchplätze nach Gewässer filtern',
    allWaterBodies: 'Alle Gewässer',
    loadingWaterBodies: 'Gewässer werden geladen...',
    loadWaterBodiesError: 'Gewässer konnten nicht geladen werden.',
    filterResultsCount: '{count} passende Einträge',
    filterResultsEmpty: 'Keine passenden Karteneinträge.',
    mapFiltersTitle: 'Kartenfilter',
    centerOnCurrentPosition: 'Auf aktuelle Position zentrieren',
    centerOnCurrentPositionShort: '◎',
    filterTypesTitle: 'Typ',
    filterTypes: {
      all: 'Alle',
      diveSite: 'Tauchplatz',
      hospital: 'Krankenhaus',
      pharmacy: 'Apotheke',
      aed: 'AED',
      airFillingStation: 'Luftfüllstation',
      rentalEquipment: 'Mietausrüstung',
      shop: 'Shop',
      sponsor: 'Sponsor',
      restaurant: 'Restaurant'
    },
    planning: {
      title: 'Tauchplanung',
      subtitle: 'Finde Tauchplätze mit genug Parkplätzen fuer deine Gruppe.',
      diversLabel: 'Anzahl Fahrzeuge',
      difficultyLabel: 'Schwierigkeitsgrad',
      selectDifficulty: 'Schwierigkeitsgrad wählen',
      targetWaterBody: 'Zielgewässer',
      selectWaterBody: 'Gewässer wählen',
      startButton: 'Planung starten',
      clearButton: 'Planung löschen',
      validationError: 'Bitte Anzahl Taucher, Schwierigkeitsgrad und Zielgewässer eingeben.',
      permissionError: 'Du hast keine Berechtigung fuer die Tauchplanung.',
      resultsTitle: 'Mögliche Tauchplätze',
      resultsCount: '{count} passende Tauchplätze',
      noResults: 'Noch keine passenden Tauchplätze fuer diese Planung gefunden.',
      parkingCapacity: '{count} Parkplätze',
      descriptionFallback: 'Keine Kurzbeschreibung verfügbar.'
    }
  },
  about: {
    kicker: 'Über uns',
    title: 'Über H₂O DiveSite Map',
    paragraphs: [
      'Liebe Tauchfreundinnen und Tauchfreunde',
      'Wer kennt das nicht: Man plant einen Tauchgang an einem neuen Platz, sucht nach Infos und findet entweder nichts, veraltete Angaben oder muss sich die Details mühsam zusammensuchen. Genau das wollen wir ändern.',
      'Wir sind ein kleines Team der Tauchschule H2O und arbeiten daran, alle Tauchplätze der Schweiz auf einer gemeinsamen Plattform zu vereinen, mit allem, was man vor dem Einstieg wissen muss. Unsere Heimat ist der Walensee, den wir bestens kennen. Aber die Schweizer Seen haben noch so viel mehr zu bieten, und dieses Wissen steckt oft in den Köpfen von Menschen wie euch.',
      'Fehlt ein Tauchplatz? Stimmt etwas nicht? Habt ihr einen Tipp, den andere nicht verpassen sollten? Schreibt uns, denn jeder Beitrag der zur Sicherheit von anderen Tauchern beiträgt, zählt.'
    ],
    signoff: 'Allzeit Guet Luft und viel Freude im Wasser!',
    team: 'Euer Team von H2O DIE Tauchschule'
  },
  visibility: {
    noValue: 'Kein Wert',
    levels: {
      verybad: 'Sehr schlecht',
      bad: 'Schlecht',
      poor: 'Mässig',
      okay: 'Okay',
      good: 'Gut',
      verygood: 'Sehr gut',
      excellent: 'Ausgezeichnet'
    },
    depths: {
      '0to5': '0 bis 5 Meter',
      '5to10': '5 bis 10 Meter',
      '10to20': '10 bis 20 Meter',
      '20to30': '20 bis 30 Meter',
      '30to40': '30 bis 40 Meter',
      moreThan40: 'Unter 40 Meter'
    },
    report: {
      title: 'Sichtweite melden',
      toOverview: 'Zur Sichtweiten-Übersicht',
      filterByName: 'Tauchplatz nach Name filtern',
      filterByNamePlaceholder: 'Tauchplatzname eingeben...',
      filterResultsCount: '{count} passende Tauchplaetze',
      filterResultsEmpty: 'Keine passenden Tauchplaetze.',
      selectDiveSite: 'Tauchplatz auswählen',
      loadingDiveSites: 'Tauchplätze werden geladen...',
      availableDiveSites: '{count} Tauchplätze verfügbar',
      dateOfDive: 'Tauchdatum',
      reportingAs: 'Meldung als',
      yourName: 'Dein Name',
      yourEmail: 'Deine E-Mail',
      abbreviation: 'Kürzel',
      reportedBy: 'Gemeldet von',
      can: 'Du kannst dich',
      registerLink: 'registrieren',
      or: 'oder',
      loginLink: 'anmelden',
      profileHint: 'dann werden die Daten aus deinem Profil verwendet.',
      divers: 'Taucher',
      notes: 'Bemerkungen',
      submitting: 'Wird gesendet...',
      submit: 'Sichtweitenmeldung senden',
      loadDiveSitesError: 'Tauchplätze konnten nicht geladen werden.',
      selectDiveSiteError: 'Bitte einen Tauchplatz auswählen.',
      selectVisibilityLevelError: 'Bitte mindestens eine Sichtweite fuer einen Tiefenbereich auswaehlen.',
      nameEmailRequired: 'Name und E-Mail sind erforderlich.',
      humanCheckNotice: 'Dieses Formular ist durch reCAPTCHA geschützt.',
      humanCheckFailed: 'Menschliche Prüfung fehlgeschlagen. Bitte erneut versuchen.',
      humanCheckUnavailable: 'Menschliche Prüfung ist derzeit nicht verfügbar.',
      submitSuccess: 'Sichtweitenmeldung gespeichert.',
      submitError: 'Sichtweitenmeldung konnte nicht gespeichert werden.'
    },
    overview: {
      title: 'Sichtweiten-Übersicht',
      subtitle: 'Berichte der letzten 14 Tage.',
      reportLink: 'Sichtweite melden',
      activeFilter: 'Ein Filter ist aktiv.',
      activeWaterBodyFilter: 'Gefiltert nach Gewässer: {name}',
      loading: 'Sichtweiten-Übersicht wird geladen...',
      noReports: 'Keine Berichte in den letzten zwei Wochen.',
      latest: 'Neueste',
      deleting: 'Wird gelöscht...',
      loadError: 'Sichtweiten-Übersicht konnte nicht geladen werden.',
      deleteConfirm: 'Diesen Sichtweitenbericht löschen?',
      deleteError: 'Bericht konnte nicht gelöscht werden.',
      columns: {
        diveSite: 'Tauchplatz',
        date: 'Datum',
        remarks: 'Info',
        delete: 'Löschen'
      }
    }
  },
  map: {
    helpReady: 'Klicke auf die Karte, um Breitengrad und Längengrad zu setzen.',
    loadingLocation: 'Standort wird ermittelt...',
    locationHint: 'Klicke auf die Karte, um Koordinaten zu wählen.',
    geolocationUnsupported: 'Geolokalisierung wird von deinem Browser nicht unterstützt.',
    locationAccessDenied: 'Standortzugriff verweigert. Fallback-Position wird verwendet.',
    mapAssetsError: 'Kartendaten konnten nicht geladen werden.'
  },
  diveSite: {
    addTitle: 'Tauchplatz hinzufügen',
    requiredFieldsError: 'Bitte Erstellt von ID, Tauchplatz Name und Tauchplatzbeschreibung ausfüllen.',
    createError: 'Tauchplatz konnte nicht erstellt werden.',
    createdById: 'Erstellt von ID',
    diveSiteName: 'Name des Tauchplatzes',
    diveSiteDescription: 'Beschreibung des Tauchplatzes',
    alternativeName1: 'Alternativer Name',
    alternativeName2: 'Alternativer Name',
    linkedWaterBody: 'Verknüpftes Gewässer',
    noLinkedWaterBody: 'Kein Gewässer verknüpft',
    loadingLinkedWaterBodies: 'Gewässer werden geladen...',
    linkedWaterBodiesAvailable: '{count} Gewässer verfügbar',
    loadLinkedWaterBodiesError: 'Gewässer konnten nicht geladen werden.',
    entryPoint: 'Einstiegspunkt',
    pickCoordinates: 'Koordinaten auf Karte wählen',
    latitude: 'Breitengrad',
    longitude: 'Längengrad',
    heightAboveSeaLevel: 'Höhe über Meeresspiegel (m)',
    difficultyLevel: 'Schwierigkeitsgrad',
    difficultyNone: 'Kein Schwierigkeitsgrad ausgewählt',
    uploadMapImage1: 'Kartenbild 1 hochladen',
    uploadMapImage2: 'Kartenbild 2 hochladen',
    uploadMapImageBathymetryNote: 'Wenn der Dateiname mit "b3d" beginnt, wird automatisch eine Farbskala fuer die swissBATHY3D-Karte eingefuegt.',
    uploadingImage1: 'Bild 1 wird hochgeladen...',
    uploadingImage2: 'Bild 2 wird hochgeladen...',
    mapDetails1: 'Map-Details 1 (ObjectId)',
    mapDetails2: 'Map-Details 2 (ObjectId)',
    editMapDetails1: 'Map-Details 1 bearbeiten',
    editMapDetails2: 'Map-Details 2 bearbeiten',
    highlights: 'Du findest hier',
    emergencyHospital: 'Krankenhaus',
    noEmergencyHospitalSelected: 'Kein Notfall-Krankenhaus ausgewählt',
    loadingHospitals: 'Krankenhäuser werden geladen...',
    hospitalsAvailable: '{count} Krankenhäuser verfügbar',
    hospitalMapHint: 'Klicke auf einen Krankenhaus-Marker auf der Karte, um ihn auszuwählen.',
    hospitalMapRequiresCoordinates: 'Setze zuerst die Tauchplatz-Koordinaten, um ein Krankenhaus auf der Karte auszuwählen.',
    hospitalMapVisibleCount: '{count} Krankenhaus-Marker werden angezeigt.',
    hospitalMapNoNearbyFacilities: 'Keine Krankenhäuser in der Nähe dieses Tauchplatzes verfügbar.',
    selectedHospital: 'Ausgewähltes Krankenhaus',
    clearSelectedHospital: 'Krankenhaus-Auswahl löschen',
    emergencyNextAed: 'Nächstes AED',
    noEmergencyNextAedSelected: 'Kein AED ausgewählt',
    loadingAedFacilities: 'AED-Einrichtungen werden geladen...',
    aedFacilitiesAvailable: '{count} AED-Einrichtungen verfügbar',
    aedMapHint: 'Klicke auf einen AED-Marker auf der Karte, um ihn auszuwählen.',
    aedMapRequiresCoordinates: 'Setze zuerst die Tauchplatz-Koordinaten, um ein AED auf der Karte auszuwählen.',
    aedMapVisibleCount: '{count} AED-Marker werden angezeigt.',
    aedMapNoNearbyFacilities: 'Keine AEDs in der Nähe dieses Tauchplatzes verfügbar.',
    selectedAed: 'Ausgewähltes AED',
    clearSelectedAed: 'AED-Auswahl löschen',
    emergencyPhone: 'Telefon',
    emergencyPhoneName: 'Name Notfalltelefon',
    emergencyPhoneNamePlaceholder: 'REGA, Rettungszentrale usw.',
    facilitiesLabel: 'Infrastruktur',
    dangers: 'Gefahren',
    directionsDescription: 'Wegbeschreibung',
    accessibilityOtherLabel: 'Sonstiger Zugang',
    loadFacilitiesError: 'Einrichtungen konnten nicht geladen werden.',
    notes: 'Notizen',
    byCar: 'Mit Auto erreichbar',
    byBoat: 'Mit Boot erreichbar',
    saving: 'Speichern...',
    createButton: 'Tauchplatz erstellen',
    form: {
      sections: {
        access: 'Zugang & Infrastruktur',
        notes: 'Notizen & Sicherheit'
      },
      parkingSpaces: 'Parkplaetze',
      recalculateParkingDistance: '↻',
      parkingDistanceHint: 'Mit Neuberechnen aus Tauchplatz- und Parkplatz-Koordinaten füllen, danach weiter manuell anpassbar.'
    },
    show: {
      backToMap: 'Zurück zur Karte',
      loading: 'Tauchplatz wird geladen...',
      fallbackTitle: 'Tauchplatz',
      deleteDiveSite: 'Tauchplatz löschen',
      downloadPdfSummary: 'PDF herunterladen',
      pdfPreparing: 'PDF wird vorbereitet...',
      pdfSummaryTitle: 'Tauchplatz-Zusammenfassung',
      pdfMapMarkersTitle: 'Kartenmarker',
      qrCodeAlt: 'QR-Code zu diesem Tauchplatz',
      pdfHospitalAddress: 'Spitaladresse',
      pdfHospitalPhone: 'Spitaltelefon',
      sections: {
        overview: 'Übersicht',
        location: 'Ort',
        accessibilityFacilities: 'Zugang & Infrastruktur',
        linkedData: 'Verknüpfte Daten',
        visibility: 'Sichtweite',
        maps: 'Karten',
        metadata: 'Metadaten'
      },
      notice: {
        warning: 'Achtung',
        reminderTitle: 'Wichtige Information',
        continueButton: 'Zum Tauchplatz',
        addTitle: 'Hinweis hinzufügen',
        editTitle: 'Hinweis bearbeiten',
        titleLabel: 'Titel',
        infoLabel: 'Information',
        startDateLabel: 'Startdatum',
        endDateLabel: 'Enddatum (optional)',
        noEndDate: 'Kein Enddatum',
        dateRange: '{start} bis {end}',
        saveError: 'Hinweis konnte nicht gespeichert werden.',
        deleteConfirm: 'Diesen Hinweis löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
        deleteError: 'Hinweis konnte nicht gelöscht werden.'
      },
      description: 'Beschreibung',
      alternativeName1: 'Alternativer Name',
      alternativeName2: 'Alternativer Name',
      entryPoint: 'Einstieg',
      dangers: 'Gefahren',
      directions: 'Wegbeschreibung',
      notes: 'Notizen',
      difficultyLevel: 'Schwierigkeitsgrad',
      difficulty: {
        beginner: 'Anfänger',
        advanced: 'Mässig',
        expert: 'Fortgeschritten'
      },
      location: 'Ort',
      coordinates: 'Koordinaten',
      nextParking: 'Nächster Parkplatz',
      nextParkingLatitude: 'Parkplatz Breitengrad',
      nextParkingLongitude: 'Parkplatz Längengrad',
      parkingType: 'Parken',
      parkingPosition: 'Parkplatzposition',
      parkingPositionTitle: 'Position des Parkplatzes',
      navigateToDiveSite: 'zu diesem Tauchplatz navigieren',
      navigateToParking: 'zu diesem Parkplatz navigieren',
      bearingNavigation: {
        title: 'Peilungs-Navigation',
        subtitle: 'Live-Richtung zum Tauchplatz',
        targetDirection: 'Ziel {direction}',
        enableCompass: 'Kompass aktivieren',
        calibrateCompass: 'Kompass kalibrieren',
        locating: 'Standort wird gesucht...',
        locationUnsupported: 'Live-Standort wird von diesem Browser nicht unterstützt.',
        compassUnavailable: 'Auf diesem Gerät sind keine Kompassdaten verfügbar.',
        compassPermissionHint: 'Erlaube den Kompasszugriff, damit der Richtungspfeil live angezeigt wird.',
        calibrationHint: 'Kompass kalibrieren, falls der Pfeil springt. Bewege das Handy dafür in einer Acht.',
        cardinals: {
          north: 'nördlich',
          northeast: 'nordöstlich',
          east: 'östlich',
          southeast: 'südöstlich',
          south: 'südlich',
          southwest: 'südwestlich',
          west: 'westlich',
          northwest: 'nordwestlich'
        }
      },
      heightAboveSeaLevel: 'Höhe über Meeresspiegel',
      byCar: 'Mit Auto erreichbar',
      byBoat: 'Mit Boot erreichbar',
      accessibilityOther: 'Sonstiger Zugang',
      onSiteFacilities: 'Infrastruktur vor Ort',
      highlights: 'Du findest hier',
      waterBodyName: 'Gewässername',
      waterBodyType: 'Gewässertyp',
      waterType: 'Wassertyp',
      emergencyFacility: 'Nächste Einrichtung',
      emergencyType: 'Typ',
      emergencyLocation: 'Standort',
      emergencyPhone: 'Telefon',
      emergencyWebsite: 'Webseite',
      emergencyNextAed: 'Nächstes AED',
      aedLocation: 'Standort',
      emergencyPhoneName: 'Name Notfalltelefon',
      safetyInfos: 'Sicherheitsinfos',
      emergencyInfos: 'Notfall Infos',
      saveChanges: 'Änderungen speichern',
      boatsTitle: 'Bootsausfahrten',
      loadingBoats: 'Bootsanbieter werden geladen...',
      loadBoatsError: 'Bootsanbieter konnten nicht geladen werden.',
      noBoatOperators: 'Für dieses Gewässer sind noch keine Bootsanbieter verknüpft.',
      bookBoatTrip: 'Bootsausflug buchen',
      operatorName: 'Anbieter',
      boatLogoAlt: 'Anbieter-Logo',
      boatImageAlt: 'Bootsbild',
      openMap: 'Karte öffnen',
      colorLegend: 'Farblegende',
      bathymetryLegendAlt: 'Bathymetrie-Farblegende',
      mapImage1Alt: 'Karte 1',
      mapImage2Alt: 'Karte 2',
      mapImage1LoadError: 'Bild 1 konnte nicht geladen werden.',
      mapImage2LoadError: 'Bild 2 konnte nicht geladen werden.',
      loadingMapDetails: 'Kartendetails werden geladen...',
      loadMapDetailsError: 'Kartendetails konnten nicht geladen werden.',
      uploadImageError: 'Bild konnte nicht hochgeladen werden.',
      updateError: 'Tauchplatz konnte nicht aktualisiert werden.',
      deleteConfirm: 'Diesen Tauchplatz löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteError: 'Tauchplatz konnte nicht gelöscht werden.',
      deleteMapDetailsConfirm: '{target} löschen? Dadurch werden das Kartenbild und die verknüpften Kartenobjekte entfernt, sobald du die Änderungen speicherst.',
      openNavigationApp: 'In Navigations-App öffnen',
      visibility: {
        loading: 'Sichtweitenberichte werden geladen...',
        none: 'Keine Sichtweitenberichte vorhanden.',
        sourceLastTwoWeeks: 'Berichte der letzten zwei Wochen.',
        sourceLatestFive: 'Keine Berichte in den letzten zwei Wochen. Es werden die 5 neuesten Berichte angezeigt.',
        infoButton: 'info'
      },
      mapImage1: 'Kartenbild 1',
      mapImage2: 'Kartenbild 2',
      mapObject: 'Kartenobjekt',
      diveSitePosition: 'Position des Tauchplatzes',
      emergencyFacilityPosition: 'Position der Notfall-Einrichtung',
      emergencyNextAedPosition: 'Position des AED',
      mapObjectPicture1: 'Kartenobjekt Bild 1',
      mapObjectPicture2: 'Kartenobjekt Bild 2',
      note1: 'Notiz 1',
      note2: 'Notiz 2',
      noLinkedPicturesOrNotes: 'Keine verknüpften Bilder oder Notizen für dieses Kartenobjekt.',
      noMapImages: 'Keine Kartenbilder verfügbar.',
      mapDetails1Id: 'Map-Details-1-ID',
      mapDetails2Id: 'Map-Details-2-ID',
      createdBy: 'Erstellt von',
      updatedBy: 'Aktualisiert von',
      created: 'Erstellt',
      updated: 'Aktualisiert',
      yes: 'Ja',
      no: 'Nein',
      parkingDistance: 'Parkdistanz',
      highlightValues: {
        wreck: 'Wrack',
        wall: 'Steilwand',
        overhang: 'Überhang',
        cave: 'Höhle',
        rocks: 'Felsen',
        object: 'Objekt',
        plants: 'Pflanzen',
        silt: 'Schlick'
      },
      facilityValues: {
        parkingFree: 'Kostenloses Parken',
        parkingPaid: 'Kostenpflichtiges Parken',
        toilet: 'Toilette',
        shelter: 'Unterstand',
        kiosk: 'Kiosk',
        restaurant: 'Restaurant',
        foodTruck: 'Food Truck',
        rentalEquipment: 'Mietausrüstung',
        airFillingStation: 'Luftfüllstation'
      },
      notFound: 'Tauchplatz nicht gefunden.',
      loadError: 'Tauchplatzdetails konnten nicht geladen werden.',
      waterBodyTypes: {
        lake: 'See',
        river: 'Fluss',
        sea: 'Meer',
        ocean: 'Ozean',
        pool: 'Pool'
      },
      waterTypes: {
        freshwater: 'Süsswasser',
        saltwater: 'Salzwasser',
        brackish: 'Brackwasser',
        chlorinated: 'Gechlort'
      }
    }
  },
  language: {
    label: 'Sprache',
    english: 'Englisch',
    german: 'Deutsch',
    italian: 'Italienisch',
    french: 'Französisch'
  },
  theme: {
    label: 'Design',
    system: 'System',
    light: 'Hell',
    dark: 'Dunkel'
  },
  admin: {
    title: 'Admin',
    intro: 'Bitte wähle den Eintrag aus, den du ändern oder löschen möchtest.',
    openStatsPage: 'Statistiken öffnen',
    publicAccessTitle: 'Öffentlicher App-Zugriff',
    publicAccessHelp: 'Besucher müssen sich anmelden, bevor sie die Tauchplatzkarte, Tauchplatzdetails oder Sichtweiten-Seiten nutzen können.',
    publicAccessOpen: 'Für Gäste offen',
    publicAccessLoginRequired: 'Login erforderlich',
    loadingSettings: 'Einstellungen werden geladen...',
    savingSettings: 'Einstellungen werden gespeichert...',
    loadSettingsError: 'Einstellungen konnten nicht geladen werden.',
    updateSettingsError: 'Einstellungen konnten nicht aktualisiert werden.',
    importTitle: '.dlexch importieren',
    importIntro: 'Lade eine DiveLogExchange-Datei hoch, um Tauchplätze gesammelt anzulegen.',
    importFileLabel: '.dlexch-Datei',
    importNoFileSelected: 'Keine Datei ausgewählt.',
    importNoFileError: 'Bitte zuerst eine .dlexch-Datei auswählen.',
    importButton: 'Datei importieren',
    importing: 'Importiere...',
    importProcessedCount: 'Place-Einträge',
    importImportedCount: 'Importiert',
    importIgnoredCount: 'Ignoriert',
    importWarningCount: 'Warnungen',
    importErrorCount: 'Fehler',
    importWarningsTitle: 'Import-Warnungen',
    importErrorsTitle: 'Import-Fehler',
    importError: 'Die .dlexch-Datei konnte nicht importiert werden.',
    usersTitle: 'Benutzer',
    waterBodiesTitle: 'Gewässer',
    diveSitesTitle: 'Tauchplätze',
    facilitiesTitle: 'Einrichtungen',
    boatsTitle: 'Boote',
    noUsersFound: 'Keine Benutzer gefunden.',
    noWaterBodiesFound: 'Keine Gewässer gefunden.',
    noDiveSitesFound: 'Keine Tauchplätze gefunden.',
    noFacilitiesFound: 'Keine Einrichtungen gefunden.',
    noBoatsFound: 'Keine Boote gefunden.',
    addWaterBody: 'Gewässer hinzufügen',
    addDiveSite: 'Tauchplatz hinzufügen',
    addFacility: 'Einrichtung hinzufügen',
    addBoat: 'Boot hinzufügen',
    filterUsers: 'Benutzer filtern',
    filterUsersPlaceholder: 'Name, E-Mail oder Rolle',
    loadingUsers: 'Benutzer werden geladen...',
    filterWaterBodies: 'Gewässer filtern',
    filterWaterBodiesPlaceholder: 'Name oder Ort',
    loadingWaterBodies: 'Gewässer werden geladen...',
    filterDiveSites: 'Tauchplätze filtern',
    filterDiveSitesPlaceholder: 'Name oder Ort',
    loadingDiveSites: 'Tauchplätze werden geladen...',
    filterFacilities: 'Einrichtungen filtern',
    filterFacilitiesPlaceholder: 'Name oder Ort',
    loadingFacilities: 'Einrichtungen werden geladen...',
    filterBoats: 'Boote filtern',
    filterBoatsPlaceholder: 'Boot, Anbieter oder Gewässer',
    loadingBoats: 'Boote werden geladen...',
    loadUsersError: 'Benutzer konnten nicht geladen werden.',
    loadWaterBodiesError: 'Gewässer konnten nicht geladen werden.',
    loadDiveSitesError: 'Tauchplätze konnten nicht geladen werden.',
    loadFacilitiesError: 'Einrichtungen konnten nicht geladen werden.',
    loadBoatsError: 'Boote konnten nicht geladen werden.',
    userDetailsTitle: 'Benutzerdetails',
    loadingUser: 'Benutzer wird geladen...',
    userNotFound: 'Benutzer nicht gefunden.',
    loadUserError: 'Benutzerdetails konnten nicht geladen werden.',
    noEditPermission: 'Du hast keine Berechtigung, diesen Benutzer zu bearbeiten.',
    changePasswordTitle: 'Passwort ändern',
    changePasswordHelp: 'Setze ein neues Passwort für diesen Benutzer. Das Passwort muss mindestens 6 Zeichen lang sein.',
    saveUser: 'Benutzer speichern',
    savePassword: 'Passwort speichern',
    deleteUser: 'Benutzer löschen',
    deleteUserConfirm: 'Diesen Benutzer endgültig löschen?',
    passwordRequired: 'Bitte gib das neue Passwort zweimal ein.',
    passwordMinLength: 'Das Passwort muss mindestens 6 Zeichen lang sein.',
    updateUserSuccess: 'Benutzer erfolgreich aktualisiert.',
    updateUserError: 'Benutzer konnte nicht aktualisiert werden.',
    updatePasswordSuccess: 'Passwort erfolgreich aktualisiert.',
    updatePasswordError: 'Passwort konnte nicht aktualisiert werden.',
    deleteUserError: 'Benutzer konnte nicht gelöscht werden.',
    statsTitle: 'Statistiken',
    statsIntro: 'Online-Werte zeigen Aktivität innerhalb der letzten {minutes} Minuten.',
    statsRefresh: 'Statistiken aktualisieren',
    statsLoading: 'Statistiken werden geladen...',
    statsLoadError: 'Statistiken konnten nicht geladen werden.',
    statsGeneratedAt: 'Zuletzt aktualisiert',
    statsOnlineTitle: 'Aktuell online',
    statsOnlineSessions: 'Aktive Sitzungen',
    statsOnlineAuthenticatedUsers: 'Angemeldete Benutzer',
    statsOnlineGuests: 'Gast-Sitzungen',
    statsOnlineAdmins: 'Admins online',
    statsOnlineSuperusers: 'Superuser online',
    statsDatabaseTitle: 'Datenbankübersicht',
    statsSponsorFacilities: 'Sponsor-Einrichtungen',
    statsSponsorLinksAvailable: 'Sponsor-Links',
    statsBoatsWithBookingLinks: 'Boote mit Trip-/Kontaktlinks',
    statsBoatClicksTitle: 'Klicks auf Boot-Links',
    statsSponsorClicksTitle: 'Klicks auf Sponsor-Links',
    statsBoatClicksTableTitle: 'Aufschlüsselung Boot-Links',
    statsSponsorClicksTableTitle: 'Aufschlüsselung Sponsor-Links',
    statsTotalClicks: 'Klicks gesamt',
    statsUniqueVisitors: 'Eindeutige Besucher',
    statsLast30Days: 'Letzte 30 Tage',
    statsLast7Days: 'Letzte 7 Tage',
    statsTarget: 'Ziellink',
    statsOperator: 'Anbieter',
    statsNoBoatLinks: 'Keine verfolgten Boot-Links gefunden.',
    statsNoSponsorLinks: 'Keine Sponsor-Links gefunden.'
  },
  waterBody: {
    titleAdd: 'Gewässer hinzufügen',
    titleEdit: 'Gewässer bearbeiten',
    submitCreate: 'Gewässer erstellen',
    submitUpdate: 'Gewässer aktualisieren',
    nameLabel: 'Gewässername',
    fallbackTitle: 'Gewässer',
    typeLabel: 'Typ',
    waterTypeLabel: 'Wassertyp',
    pickCoordinates: 'Koordinaten auf Karte wählen',
    loadUsersError: 'Benutzer konnten nicht geladen werden.',
    loadError: 'Gewässer konnte nicht geladen werden.',
    createError: 'Gewässer konnte nicht erstellt werden.',
    updateError: 'Gewässer konnte nicht aktualisiert werden.',
    requiredWithUser: 'Bitte Benutzer unter "Erstellt von" auswählen und Name ausfüllen.',
    requiredName: 'Bitte Name ausfüllen.',
    unsavedTitle: 'Ungespeicherte Änderungen verwerfen?',
    unsavedText: 'Du hast ungespeicherte Änderungen. Wenn du jetzt gehst, gehen sie verloren.',
    leavePage: 'Seite verlassen',
    stayPage: 'Auf Seite bleiben',
    types: {
      lake: 'See',
      river: 'Fluss',
      sea: 'Meer',
      ocean: 'Ozean',
      pool: 'Pool',
      other: 'Andere'
    },
    waterTypes: {
      freshwater: 'Süsswasser',
      saltwater: 'Salzwasser',
      brackish: 'Brackwasser',
      chlorinated: 'Gechlort'
    }
  },
  facility: {
    titleAdd: 'Einrichtung hinzufügen',
    titleEdit: 'Einrichtung bearbeiten',
    submitCreate: 'Einrichtung erstellen',
    submitUpdate: 'Einrichtung aktualisieren',
    nameLabel: 'Einrichtungsname',
    fallbackTitle: 'Einrichtung',
    pickCoordinates: 'Koordinaten auf Karte wählen',
    loadUsersError: 'Benutzer konnten nicht geladen werden.',
    loadError: 'Einrichtung konnte nicht geladen werden.',
    createError: 'Einrichtung konnte nicht erstellt werden.',
    updateError: 'Einrichtung konnte nicht aktualisiert werden.',
    requiredWithUser: 'Bitte Benutzer unter "Erstellt von" auswählen und Name ausfüllen.',
    requiredName: 'Bitte Name ausfüllen.',
    logoFileLabel: 'Logo-Datei',
    logoFilePlaceholder: 'beispiel-logo.svg',
    logoFileHelp: 'Dateiname aus /sponsors, inklusive .png oder .svg.'
  },
  boat: {
    titleAdd: 'Boot hinzufügen',
    titleEdit: 'Boot bearbeiten',
    submitCreate: 'Boot erstellen',
    submitUpdate: 'Boot aktualisieren',
    nameLabel: 'Bootsname',
    operatorNameLabel: 'Name Anbieter',
    waterBodyLabel: 'Verknüpftes Gewässer',
    selectWaterBody: 'Gewässer auswählen',
    pickCoordinates: 'Koordinaten auf Karte wählen',
    webLinkLabel: 'Webseite Anbieter',
    logoUrlLabel: 'Logo-URL',
    logoUploadLabel: 'Logo hochladen',
    boatImageUrlLabel: 'Bild-URL Boot',
    boatImageUploadLabel: 'Bootsbild hochladen',
    whatsappLinkLabel: 'WhatsApp-Link',
    bookingLinkLabel: 'Buchungslink',
    phoneLinkLabel: 'Telefon-Link',
    uploadingLogo: 'Logo wird hochgeladen...',
    uploadingBoatImage: 'Bootsbild wird hochgeladen...',
    uploadImageError: 'Bild konnte nicht hochgeladen werden.',
    logoPreviewAlt: 'Logo-Vorschau',
    boatImagePreviewAlt: 'Vorschau Bootsbild',
    deleteLogo: 'Logo löschen',
    deleteBoatImage: 'Bootsbild löschen',
    imagePathPlaceholder: '/api/v1/map-details/.../image oder https://...',
    imagePathHelp: 'Optional. Hochgeladene Bilder füllen dieses Feld automatisch. Relative /api/v1/... Pfade sind erlaubt.',
    loadUsersError: 'Benutzer konnten nicht geladen werden.',
    loadWaterBodiesError: 'Gewässer konnten nicht geladen werden.',
    loadError: 'Boot konnte nicht geladen werden.',
    createError: 'Boot konnte nicht erstellt werden.',
    updateError: 'Boot konnte nicht aktualisiert werden.',
    requiredWithUser: 'Bitte Benutzer unter "Erstellt von" auswählen sowie Bootsname und Gewässer ausfüllen.',
    requiredNameAndWaterBody: 'Bitte Bootsname und Gewässer ausfüllen.'
  },
  mapDetailsEditor: {
    defaultTitle: 'Kartendetails bearbeiten',
    intro: 'Klicke auf das Bild, um ein Kartenobjekt hinzuzufügen. Klicke auf einen Marker, um es zu bearbeiten.',
    noMapDetailImage: 'Lade zuerst ein Kartendetailbild hoch oder hinterlege eines.',
    loadingObjects: 'Kartenobjekte werden geladen...',
    imageAlt: 'Kartendetails',
    objectFallback: 'Kartenobjekt',
    imageMissing: 'Die URL des Kartenbildes fehlt. Lade zuerst ein Kartenbild hoch.',
    imageLoadError: 'Kartenbild konnte nicht geladen werden.',
    editObject: 'Kartenobjekt bearbeiten',
    newObject: 'Neues Kartenobjekt',
    positionOnMap: 'Position auf der Karte (%)',
    uploadPicture1: 'Bild 1 hochladen',
    uploadPicture2: 'Bild 2 hochladen',
    uploadingPicture1: 'Bild 1 wird hochgeladen...',
    uploadingPicture2: 'Bild 2 wird hochgeladen...',
    picture1Preview: 'Vorschau Bild 1',
    picture2Preview: 'Vorschau Bild 2',
    deletePicture1: 'Bild 1 löschen',
    deletePicture2: 'Bild 2 löschen',
    picture1Note: 'Notiz zu Bild 1',
    picture2Note: 'Notiz zu Bild 2',
    saveObject: 'Kartenobjekt speichern',
    selectHint: 'Wähle einen Marker oder klicke auf das Kartenbild, um ein Kartenobjekt hinzuzufügen.',
    loadObjectsError: 'Kartenobjekte konnten nicht geladen werden.',
    uploadNoLinkError: 'Der Upload hat keinen Link zurückgegeben.',
    uploadError: 'Bild konnte nicht hochgeladen werden.',
    missingMapDetailId: 'Keine Map-Detail-ID gesetzt.',
    nameRequired: 'Name ist erforderlich.',
    coordinatesRequired: 'Koordinaten müssen Zahlen sein.',
    saveError: 'Kartenobjekt konnte nicht gespeichert werden.',
    deleteError: 'Kartenobjekt konnte nicht gelöscht werden.'
  }
};

const messages = {
  en: enMessages,
  de: deMessages,
  it: {
    ...enMessages,
    nav: {
      ...enMessages.nav,
      home: 'Mappa',
      about: 'Chi siamo',
      addDiveSite: 'Aggiungi sito',
      login: 'Accedi',
      logout: 'Esci',
      editProfile: 'Modifica profilo',
      visibility: 'Visibilita',
      reportVisibility: 'Segnala'
    },
    language: {
      ...enMessages.language,
      label: 'Lingua',
      italian: 'Italiano',
      french: 'Francese',
      german: 'Tedesco'
    },
    theme: {
      ...enMessages.theme,
      label: 'Tema',
      system: 'Sistema',
      light: 'Chiaro',
      dark: 'Scuro'
    },
    main: {
      ...enMessages.main,
      title: 'Pagina principale',
      welcome: "Benvenuto nell'app.",
      filterByName: 'Filtra per nome',
      visibilityOverviewLink: 'Panoramica visibilita',
      reportVisibilityLink: 'Segnala visibilita immersione'
    },
    about: {
      ...enMessages.about,
      kicker: 'Chi siamo',
      title: 'Chi siamo',
      paragraphs: [
        'Care amiche e cari amici subacquei',
        'Lo conosciamo tutti: si pianifica un\'immersione in un posto nuovo, si cercano informazioni e non si trova nulla, oppure si trovano dati superati, o bisogna raccogliere i dettagli con fatica da fonti diverse. È proprio questo che vogliamo cambiare.',
        'Siamo un piccolo team di H2O DIE Tauchschule e stiamo lavorando per riunire tutti i siti d\'immersione della Svizzera su una piattaforma comune, con tutto cio che bisogna sapere prima di entrare in acqua. La nostra casa e il Walensee, che conosciamo molto bene. Ma i laghi svizzeri hanno molto altro da offrire, e spesso questa conoscenza si trova nella testa di persone come voi.',
        'Manca un sito d\'immersione? Qualcosa non e corretto? Avete un consiglio che gli altri non dovrebbero perdere? Scriveteci, perche ogni contributo che aiuta la sicurezza di altri subacquei conta.'
      ],
      signoff: 'Sempre buona aria e tanta gioia in acqua!',
      team: 'Il vostro team di H2O DIE Tauchschule'
    },
    diveSite: {
      ...enMessages.diveSite,
      show: {
        ...enMessages.diveSite.show,
        notice: {
          ...enMessages.diveSite.show.notice,
          warning: 'Avviso',
          reminderTitle: 'Informazioni importanti',
          continueButton: 'Continua al sito d\'immersione',
          addTitle: 'Aggiungi avviso',
          editTitle: 'Modifica avviso',
          titleLabel: 'Titolo',
          infoLabel: 'Informazioni',
          startDateLabel: 'Data di inizio',
          endDateLabel: 'Data di fine (facoltativa)',
          noEndDate: 'Nessuna data di fine',
          dateRange: '{start} - {end}',
          saveError: 'Impossibile salvare l\'avviso.',
          deleteConfirm: 'Eliminare questo avviso? Questa azione non può essere annullata.',
          deleteError: 'Impossibile eliminare l\'avviso.'
        }
      }
    },
    visibility: {
      ...enMessages.visibility,
      report: {
        ...enMessages.visibility.report,
        title: 'Segnala visibilita immersione',
        submit: 'Invia segnalazione visibilita'
      },
      overview: {
        ...enMessages.visibility.overview,
        title: 'Panoramica visibilita',
        columns: {
          ...enMessages.visibility.overview.columns,
          remarks: 'Info'
        }
      }
    }
  },
  fr: {
    ...enMessages,
    nav: {
      ...enMessages.nav,
      home: 'Carte',
      about: 'A propos',
      addDiveSite: 'Ajouter un site',
      login: 'Connexion',
      logout: 'Deconnexion',
      editProfile: 'Modifier le profil',
      visibility: 'Visibilite',
      reportVisibility: 'Signaler'
    },
    language: {
      ...enMessages.language,
      label: 'Langue',
      italian: 'Italien',
      french: 'Francais',
      german: 'Allemand'
    },
    theme: {
      ...enMessages.theme,
      label: 'Theme',
      system: 'Systeme',
      light: 'Clair',
      dark: 'Sombre'
    },
    main: {
      ...enMessages.main,
      title: 'Page principale',
      welcome: "Bienvenue dans l'application.",
      filterByName: 'Filtrer par nom',
      visibilityOverviewLink: 'Vue d\'ensemble visibilite',
      reportVisibilityLink: 'Signaler la visibilite'
    },
    about: {
      ...enMessages.about,
      kicker: 'A propos',
      title: 'A propos',
      paragraphs: [
        'Chères amies plongeuses, chers amis plongeurs',
        'Nous connaissons tous cette situation: on planifie une plongée sur un nouveau site, on cherche des informations et l\'on ne trouve rien, seulement des données dépassées, ou il faut rassembler les détails laborieusement. C\'est exactement ce que nous voulons changer.',
        'Nous sommes une petite équipe de H2O DIE Tauchschule et nous travaillons à réunir tous les sites de plongée de Suisse sur une plateforme commune, avec tout ce qu\'il faut savoir avant la mise à l\'eau. Notre base est le Walensee, que nous connaissons très bien. Mais les lacs suisses ont encore tellement plus à offrir, et ce savoir se trouve souvent dans la tête de personnes comme vous.',
        'Un site de plongée manque? Quelque chose est incorrect? Vous avez une astuce que les autres ne devraient pas manquer? Écrivez-nous, car chaque contribution qui aide à améliorer la sécurité des autres plongeurs compte.'
      ],
      signoff: 'Toujours bonne plongée et beaucoup de plaisir dans l\'eau!',
      team: 'Votre équipe de H2O DIE Tauchschule'
    },
    diveSite: {
      ...enMessages.diveSite,
      show: {
        ...enMessages.diveSite.show,
        notice: {
          ...enMessages.diveSite.show.notice,
          warning: 'Avertissement',
          reminderTitle: 'Informations importantes',
          continueButton: 'Continuer vers le site de plongée',
          addTitle: 'Ajouter un avertissement',
          editTitle: 'Modifier l\'avertissement',
          titleLabel: 'Titre',
          infoLabel: 'Informations',
          startDateLabel: 'Date de début',
          endDateLabel: 'Date de fin (facultative)',
          noEndDate: 'Sans date de fin',
          dateRange: '{start} - {end}',
          saveError: 'Impossible d\'enregistrer l\'avertissement.',
          deleteConfirm: 'Supprimer cet avertissement ? Cette action est irréversible.',
          deleteError: 'Impossible de supprimer l\'avertissement.'
        }
      }
    },
    visibility: {
      ...enMessages.visibility,
      report: {
        ...enMessages.visibility.report,
        title: 'Signaler la visibilite',
        submit: 'Envoyer le rapport de visibilite'
      },
      overview: {
        ...enMessages.visibility.overview,
        title: 'Vue d\'ensemble visibilite',
        columns: {
          ...enMessages.visibility.overview.columns,
          remarks: 'Infos'
        }
      }
    }
  }
};

function normalizeLocale(value) {
  if (!value || typeof value !== 'string') return '';
  return value.toLowerCase().split('-')[0];
}

function getStoredLocale() {
  try {
    return localStorage.getItem(LOCALE_STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
}

function detectBrowserLocale() {
  const browserLocales = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : [navigator.language];

  for (const candidate of browserLocales) {
    const normalized = normalizeLocale(candidate);
    if (SUPPORTED_LOCALES.includes(normalized)) return normalized;
  }

  return DEFAULT_LOCALE;
}

function resolveInitialLocale() {
  const stored = normalizeLocale(getStoredLocale());
  if (SUPPORTED_LOCALES.includes(stored)) return stored;
  return detectBrowserLocale();
}

const locale = ref(resolveInitialLocale());

function setLocale(nextLocale) {
  const normalized = normalizeLocale(nextLocale);
  const resolved = SUPPORTED_LOCALES.includes(normalized) ? normalized : DEFAULT_LOCALE;
  locale.value = resolved;
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, resolved);
  } catch (error) {
    // Ignore storage failures in restricted environments.
  }
}

function getMessage(language, key) {
  return key.split('.').reduce((accumulator, segment) => {
    if (!accumulator || typeof accumulator !== 'object') return undefined;
    return accumulator[segment];
  }, messages[language]);
}

function formatMessage(message, params = {}) {
  if (typeof message !== 'string') return message;
  return message.replace(/\{(\w+)\}/g, (_, token) => {
    return Object.prototype.hasOwnProperty.call(params, token) ? params[token] : `{${token}}`;
  });
}

function t(key, params) {
  const raw = getMessage(locale.value, key) || getMessage(DEFAULT_LOCALE, key) || key;
  return formatMessage(raw, params);
}

const i18nContext = {
  locale,
  setLocale,
  t,
  supportedLocales: SUPPORTED_LOCALES
};

const i18nPlugin = {
  install(app) {
    app.provide('i18n', i18nContext);
    app.config.globalProperties.$t = t;
    app.config.globalProperties.$locale = locale;
  }
};

export function useI18n() {
  const context = inject('i18n');
  if (!context) {
    throw new Error('i18n plugin is not installed');
  }
  return context;
}

export default i18nPlugin;
